import { useEffect, useState } from 'react';
import {
  Burger,
  Button,
  Collapse,
  Drawer,
  Group,
  rem,
  ScrollArea,
  Text,
  UnstyledButton,
} from '@mantine/core';
import {
  IconChevronRight,
  IconDeviceDesktop,
  IconDevices,
  IconEye,
  IconReportAnalytics,
  IconSettings,
  IconUserScreen,
  IconWorld,
} from '@tabler/icons-react';
import classes from './Sidebar.module.css';
import axios from 'axios';
import EnergyReportModal from './EnergyReportModal';

const data = [
  {
    label: 'Quotes',
    className: classes.specialRow,
    link: '/admin/quote/quote/',
  },
  {
    label: 'Campaigns',
    className: classes.specialRow,
    link: '/admin/broadsign/campaign/',
  },
  {
    label: 'Projects',
    className: classes.specialRow,
    link: '/admin/projects/project/',
  },
  {
    label: 'Invoices',
    className: classes.specialRow,
    link: '/admin/invoice/invoice/',
  },

  {
    label: 'New Quote v2',
    button: 'filled',
    margin: '15px 0 5px 0',
    link: '/ui/mediaplan/',
  },
  {
    label: 'New Quote',
    button: 'outline',
    margin: '0 0 15px 0',
    link: '/admin/mediaplan/',
  },

  {
    label: 'Reports',
    icon: IconReportAnalytics,
    links: [
      { label: 'Quote report', link: '/report/quotes/' },
      { label: 'Reservation report', link: '/report/reservation/' },
      { label: 'Reservation report v2', link: '/ui/report/reservations/' },
    ],
  },
  {
    label: 'Traffic',
    icon: IconDeviceDesktop,
    links: [
      { label: 'Quotes', link: '/admin/quote/quote/' },
      { label: 'Interactive Quotes', link: '/admin/quote/quote/?quote_type__exact=interactive' },
      { label: 'Campaigns', link: '/admin/broadsign/campaign/' },
      { label: 'Manual schedule', link: '/admin/magicinfo/schedule/' },
      { label: 'Rules MagicInfo', link: '/admin/magicinfo/rule/' },
      { label: 'Locations', link: '/admin/totem/totem/' },
      { label: 'Screens', link: '/admin/totem/screen/?is_active__exact=1' },
      { label: 'Postbuy days', link: '/admin/postbuy/postbuyday/' },
      { label: 'Postbuy app', link: '/admin/postbuy/postbuymobileapp/' },
      {
        label: 'Postbuy app photos',
        link: '/admin/postbuy/postbuymobileappphoto/',
      },
    ],
  },
  {
    label: 'Management',
    icon: IconSettings,
    links: [
      { label: 'Locations', link: '/admin/totem/totem/' },
      { label: 'Screens', link: '/admin/totem/screen/?is_active__exact=1' },
      { label: 'Customers', link: '/admin/quote/customer/?is_active__exact=1' },
      { label: 'Invoice rules', link: '/admin/quote/invoicerule/' },
      { label: 'Cities', link: '/admin/totem/city/' },
      { label: 'Campaigns', link: '/admin/broadsign/campaign/' },
      //{ label: 'Presets', link: '/admin/totem/preset/' },
      //{
      //  label: 'Other items templates',
      //  link: '/admin/quote/otheritemstemplate/',
      //},
      { label: 'Projects', link: '/admin/projects/project/' },
    ],
  },
  {
    label: 'Broadsign',
    icon: IconUserScreen,
    links: [{ label: 'Display units', link: '/admin/broadsign/displayunit/' }],
  },
  {
    label: 'Web',
    icon: IconWorld,
    links: [
      { label: 'Blog', link: '/admin/blog/article/' },
      { label: 'Portfolio', link: '/admin/www/portfolio/' },
      { label: 'Options', link: '/admin/homepage/options/' },
      { label: 'Newsletter', link: '/admin/homepage/newslettersubscriber/' },
      { label: 'Files', link: '/admin/homepage/file/' },
      { label: 'Testimonial', link: '/admin/homepage/testimonial/' },
    ],
  },
  {
    label: 'Audit',
    icon: IconEye,
    links: [{ label: 'Audit', link: '/admin/audit/audit/' }],
  },
];

interface LinksGroupProps {
  icon?: React.FC<any>;
  label: string;
  className?: string;
  button?: string;
  margin?: string;
  initiallyOpened?: boolean;
  link?: string;
  links?: { label: string; link: string }[];
}

function LinksGroup({
  icon: Icon,
  label,
  className,
  button,
  margin,
  initiallyOpened,
  link,
  links,
}: LinksGroupProps) {
  const hasLinks = Array.isArray(links);
  const [opened, setOpened] = useState(initiallyOpened || false);

  const items = (hasLinks ? links : []).map((linkItem) => (
    <Text<'a'>
      component="a"
      className={classes.link}
      href={linkItem.link}
      key={linkItem.label}
    >
      {translateMenu(linkItem.label)}
    </Text>
  ));

  if (!hasLinks && button) {
    let cls = className ? `${classes.row} ${className}` : classes.row;
    return (
      <Group className={cls} pl={35} pr={35} m={margin}>
        <Button
          w="100%"
          color="#50a040"
          style={{ color: button === 'outline' ? '#50a040' : 'white' }}
          component="a"
          href={link}
          variant={button}
        >
          {translateMenu(label)}
        </Button>
      </Group>
    );
  }

  if (!hasLinks && link) {
    let cls = className ? `${classes.row} ${className}` : classes.row;
    return (
      <Group className={cls} pl={Icon ? 0 : 35}>
        {Icon && <Icon className={classes.icon} />}
        <Text<'a'> component="a" className={classes.withoutLinks} href={link}>
          {translateMenu(label)}
        </Text>
      </Group>
    );
  }

  return (
    <>
      <UnstyledButton
        onClick={() => setOpened((o) => !o)}
        className={classes.control}
      >
        <Group justify="space-between" gap={0}>
          <Group className={classes.row}>
            {Icon && <Icon className={classes.icon} />}
            <Text className={`${hasLinks ? classes.withLinks : ''}`}>
              {translateMenu(label)}
            </Text>
          </Group>

          {hasLinks && (
            <IconChevronRight
              className={classes.chevron}
              stroke={1.5}
              style={{
                width: rem(16),
                height: rem(16),
                transform: opened ? 'rotate(-90deg)' : 'none',
              }}
            />
          )}
        </Group>
      </UnstyledButton>
      {hasLinks ? <Collapse in={opened}>{items}</Collapse> : null}
    </>
  );
}

interface MagicInfoProps {
  icon?: React.FC<any>;
  label: string;
  initiallyOpened?: boolean;
}

function MagicInfo({ icon: Icon, label, initiallyOpened }: MagicInfoProps) {
  const [opened, setOpened] = useState(initiallyOpened || false);

  const handleDeviceSync = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        '/admin/mf/actions/sync_magicinfo_devices/'
      );

      if (response.status === 200) {
        console.log('Devices synced successfully', response.data);
      } else {
        console.error('Error syncing devices:', response.statusText);
      }
    } catch (error) {
      console.error('Error during the fetch operation:', error);
    }
  };

  const handlePlaylistSync = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        '/admin/mf/actions/sync_magicinfo_playlists/'
      );

      if (response.status === 200) {
        console.log('Devices synced successfully', response.data);
      } else {
        console.error('Error syncing devices:', response.statusText);
      }
    } catch (error) {
      console.error('Error during the fetch operation:', error);
    }
  };

  return (
    <>
      <UnstyledButton
        onClick={() => setOpened((o) => !o)}
        className={classes.control}
      >
        <Group justify="space-between" gap={0}>
          <Group className={classes.row}>
            {Icon && <Icon className={classes.icon} />}
            <Text className={classes.withLinks}>{label}</Text>
          </Group>

          <IconChevronRight
            className={classes.chevron}
            stroke={1.5}
            style={{
              width: rem(16),
              height: rem(16),
              transform: opened ? 'rotate(-90deg)' : 'none',
            }}
          />
        </Group>
      </UnstyledButton>

      <Collapse in={opened}>
        <form method="post">
          <div className={classes.magicinfo}>
            <Text<'a'>
              component="a"
              href="/admin/magicinfo/magicinfodevice/"
              className={classes.link}
            >
              Devices
            </Text>
            <Button
              type="submit"
              size="xs"
              onClick={handleDeviceSync}
              className={classes.fetchButton}
            >
              Fetch
            </Button>
          </div>

          <div className={classes.magicinfo}>
            <Text<'a'>
              component="a"
              href="/admin/magicinfo/magicinfoplaylist/"
              className={classes.link}
            >
              Playlists
            </Text>
            <Button
              type="submit"
              size="xs"
              onClick={handlePlaylistSync}
              className={classes.fetchButton}
            >
              Fetch
            </Button>
          </div>
        </form>
      </Collapse>
    </>
  );
}

export default function Sidebar({ color }: { color?: string }) {
  const links = data.map((item) => <LinksGroup {...item} key={item.label} />);
  const [sidebarVisible, setSidebarVisible] = useState(false);

  useEffect(() => {
    const hamburgerButton = document.getElementById('branding');

    if (hamburgerButton) {
      const handleHamburgerClick = () => {
        setSidebarVisible(!sidebarVisible);
      };

      hamburgerButton.addEventListener('click', handleHamburgerClick);

      return () => {
        hamburgerButton.removeEventListener('click', handleHamburgerClick);
      };
    }
  }, [sidebarVisible]);

  return (
    <>
      <Burger opened={false} color={color || 'white'} size="md" id="branding" />
      <Drawer
        opened={sidebarVisible}
        onClose={() => setSidebarVisible(false)}
        trapFocus={false}
        size="xs"
        withCloseButton={false}
      >
        <ScrollArea className={classes.section} scrollbars="y">
          <div className={classes.linksInner}>{links}</div>
          <MagicInfo icon={IconDevices} label="Magicinfo" />
          <EnergyReportModal />
        </ScrollArea>
      </Drawer>
    </>
  );
}

function translateMenu(label: string): string {
  const t: { [key: string]: string } = {
    Quotes: 'Ponude',
    'Interactive Quotes': 'Interaktivne ponude',
    Campaigns: 'Kampanje',
    Projects: 'Projekti',
    Invoices: 'Računi',
    'New Quote': 'Nova ponuda v1',
    'New Quote v2': 'Nova ponuda v2',
    Reports: 'Izvještaji',
    'Quote report': 'Ponude',
    'Reservation report': 'Rezervacije',
    'Reservation report v2': 'Rezervacije v2',
    'Rules MagicInfo': 'Pravila za MagicInfo',
    Locations: 'Lokacije',
    Screens: 'Ekrani',
    'Postbuy days': 'Postbuy dani',
    Management: 'Upravljanje',
    Customers: 'Klijenti i agencije',
    'Invoice rules': 'Pravila računa',
    Cities: 'Gradovi',
  };
  return t[label] || label;
}
